// Calculate monthly cashflows for a portfolio
export const calculatePortfolioCashflows = (portfolio, investmentAmount) => {
  try {
    if (!portfolio || !portfolio.projects || !Array.isArray(portfolio.projects) || investmentAmount <= 0) {
      return new Array(42).fill(0);
    }
    
    const maxExitMonth = Math.max(...portfolio.projects.map(p => p.exitMonth || 42));
    const cashflows = new Array(maxExitMonth).fill(0);
    
    portfolio.projects.forEach(project => {
      if (!project || typeof project.capitalDistribution !== 'number' || typeof project.interestRate !== 'number') {
        return;
      }
      
      // Validate project values
      const startMonth = Math.max(1, project.startMonth || 1);
      const exitMonth = Math.max(startMonth, project.exitMonth || (startMonth + 36));
      const capitalDistribution = Math.max(0, Math.min(1, project.capitalDistribution));
      const interestRate = Math.max(0, project.interestRate);
      const exitMultiple = project.exitMultiple || 1.0;
      
      // Calculate project investment based on capital distribution (weight)
      const projectInvestment = investmentAmount * capitalDistribution;
      
      // Investment outflow at start month (zero-based)
      if (startMonth > 0 && startMonth <= maxExitMonth) {
        cashflows[startMonth - 1] -= projectInvestment;
      }
      
      // Interest payments (monthly) based on the project investment
      for (let month = startMonth; month < exitMonth && month <= maxExitMonth; month++) {
        const monthlyInterest = projectInvestment * interestRate / 12;
        cashflows[month - 1] += monthlyInterest;
      }
      
      // Capital return at exit month with exit multiple (if applicable)
      if (exitMonth > 0 && exitMonth <= maxExitMonth) {
        // Apply exit multiple only for Venture Debt portfolio (id: 2)
        const exitAmount = portfolio.id === 2 ? projectInvestment * exitMultiple : projectInvestment;
        cashflows[exitMonth - 1] += exitAmount;
      }
    });
    
    return cashflows;
  } catch (error) {
    console.error('Error calculating portfolio cashflows:', error);
    return new Array(42).fill(0);
  }
};

// Calculate IRR using Newton-Raphson method
export const calculateIRR = (cashflows, guess = 0.1, maxIterations = 100, tolerance = 1e-6) => {
  try {
    // Check if we have valid cashflows
    if (!cashflows || !Array.isArray(cashflows) || cashflows.length === 0) {
      return 0;
    }
    
    // Convert any non-number values to 0 and ensure we're working with valid numbers
    const validCashflows = cashflows.map(cf => typeof cf === 'number' && isFinite(cf) ? cf : 0);
    
    // Check if all cashflows are zero
    const totalCashflow = validCashflows.reduce((sum, cf) => sum + cf, 0);
    if (Math.abs(totalCashflow) < tolerance) {
      return 0;
    }
    
    // Check if we have at least one positive and one negative cashflow
    const hasPositive = validCashflows.some(cf => cf > 0);
    const hasNegative = validCashflows.some(cf => cf < 0);
    if (!hasPositive || !hasNegative) {
      return 0;
    }
    
    let rate = guess;
    
    for (let i = 0; i < maxIterations; i++) {
      let npv = 0;
      let derivative = 0;
      
      for (let t = 0; t < validCashflows.length; t++) {
        const cashflow = validCashflows[t];
        if (Math.abs(cashflow) < tolerance) continue;
        
        const discountFactor = Math.pow(1 + rate, -(t + 1));
        npv += cashflow * discountFactor;
        derivative -= (t + 1) * cashflow * discountFactor / (1 + rate);
      }
      
      if (Math.abs(npv) < tolerance) {
        return Math.max(-0.99, Math.min(rate, 10));
      }
      
      // Check for division by zero or invalid derivative
      if (Math.abs(derivative) < tolerance) {
        return 0;
      }
      
      const newRate = rate - npv / derivative;
      
      // Check for convergence
      if (Math.abs(newRate - rate) < tolerance) {
        return Math.max(-0.99, Math.min(newRate, 10));
      }
      
      // Check for reasonable bounds
      if (newRate < -0.99 || newRate > 10 || !isFinite(newRate)) {
        return 0;
      }
      
      rate = newRate;
    }
    
    return Math.max(-0.99, Math.min(rate, 10));
  } catch (error) {
    console.error('Error calculating IRR:', error);
    return 0;
  }
};

// Calculate combined cashflows from multiple portfolios
export const calculateCombinedCashflows = (portfoliosData, investmentAmounts) => {
  try {
    if (!portfoliosData || !Array.isArray(portfoliosData) || !investmentAmounts || !Array.isArray(investmentAmounts)) {
      return new Array(42).fill(0);
    }
    
    let maxExitMonth = 42;
    portfoliosData.forEach(portfolio => {
      if (portfolio && portfolio.projects) {
        const portfolioMaxExit = Math.max(...portfolio.projects.map(p => p.exitMonth || 42));
        maxExitMonth = Math.max(maxExitMonth, portfolioMaxExit);
      }
    });
    
    const combinedCashflows = new Array(maxExitMonth).fill(0);
    
    portfoliosData.forEach((portfolio, index) => {
      if (portfolio && investmentAmounts[index] > 0) {
        const portfolioCashflows = calculatePortfolioCashflows(portfolio, investmentAmounts[index]);
        portfolioCashflows.forEach((cashflow, month) => {
          if (typeof cashflow === 'number' && isFinite(cashflow) && month < maxExitMonth) {
            combinedCashflows[month] += cashflow;
          }
        });
      }
    });
    
    return combinedCashflows;
  } catch (error) {
    console.error('Error calculating combined cashflows:', error);
    return new Array(42).fill(0);
  }
};

// Format currency
export const formatCurrency = (amount) => {
  try {
    if (typeof amount !== 'number' || !isFinite(amount)) {
      return '$0';
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  } catch (error) {
    console.error('Error formatting currency:', error);
    return '$0';
  }
};

// Format percentage
export const formatPercentage = (value) => {
  try {
    if (typeof value !== 'number' || !isFinite(value)) {
      return '0.00%';
    }
    return `${(value * 100).toFixed(2)}%`;
  } catch (error) {
    console.error('Error formatting percentage:', error);
    return '0.00%';
  }
}; 